import React, { useState, useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const Trimming = ({ formData = {}, onFormDataChange }) => {
    // Destructure formData to get initial values
    const {
        trimmingObservations = [],
        visualObservations = [],
        nonConformance = '',
    } = formData;

    // State to hold table data (product, specifications, frequency)
    const [trimmingData, setTrimmingData] = useState([]);
    const [visualInspectionData, setVisualInspectionData] = useState([]);

    // Fetch metadata and inspectionData only once on component mount
    useEffect(() => {
        const metadata = JSON.parse(sessionStorage.getItem('metadata'));
        const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));

        // Initialize table data from metadata
        if (metadata && metadata.post && metadata.post.Trimming_post) {
            const { Trimming, VisualInspection } = metadata.post.Trimming_post;
            setTrimmingData(Trimming || []);
            setVisualInspectionData(VisualInspection || []);
        }

        // Initialize observations and non-conformance from inspectionData only if formData is empty
        if (
            inspectionData &&
            inspectionData.PostLamination &&
            inspectionData.PostLamination.Trimming_post &&
            (!trimmingObservations.length && !visualObservations.length && !nonConformance.trim())
        ) {
            const { Trimming, VisualInspection, nonConformance: inspectionNonConformance } =
                inspectionData.PostLamination.Trimming_post;

            // Map observation data from inspectionData
            const prefilledTrimmingObservations = Trimming?.map((item) => ({
                observation1: item.observation1 || '',
                observation2: item.observation2 || '',
            })) || [];

            const prefilledVisualObservations = VisualInspection?.map((item) => ({
                observation1: item.observation1 || '',
                observation2: item.observation2 || '',
            })) || [];

            // Update formData with observations and non-conformance
            onFormDataChange({
                ...formData,
                trimmingObservations: prefilledTrimmingObservations,
                visualObservations: prefilledVisualObservations,
                nonConformance: inspectionNonConformance || '',
            });
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    const handleObservationChange = (index, field, value, isTrimming) => {
        const fieldKey = isTrimming ? 'trimmingObservations' : 'visualObservations';
        const updatedObservations = [...(formData[fieldKey] || [])];
        updatedObservations[index] = {
            ...updatedObservations[index],
            [field]: value,
        };
        onFormDataChange({ ...formData, [fieldKey]: updatedObservations });
    };

    const handleNonConformanceChange = (e) => {
        onFormDataChange({ ...formData, nonConformance: e.target.value });
    };

    const submitObservations = async () => {
        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

        // Combine table data with observations
        const combinedTrimmingData = trimmingData.map((item, index) => ({
            product: item.product || '',
            specifications: item.specifications || '',
            frequency: item.frequency || '',
            observation1: formData.trimmingObservations?.[index]?.observation1 || '',
            observation2: formData.trimmingObservations?.[index]?.observation2 || '',
        }));

        const combinedVisualInspectionData = visualInspectionData.map((item, index) => ({
            product: item.product || '',
            specifications: item.specifications || '',
            frequency: item.frequency || '',
            observation1: formData.visualObservations?.[index]?.observation1 || '',
            observation2: formData.visualObservations?.[index]?.observation2 || '',
        }));

        const payload = {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            status: 'completed',
            PostLamination: {
                Trimming_post: {
                    Trimming: combinedTrimmingData,
                    VisualInspection: combinedVisualInspectionData,
                    nonConformance: formData.nonConformance || '',
                },
            },
        };

        try {
            await submitFormData(payload);
            alert('Observations submitted successfully');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations');
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">Trimming Inspection</h3>

            {/* Trimming Table */}
            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Trimming</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {trimmingData.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100`}
                            >
                                <td className="py-3 px-4">{item.product || ''}</td>
                                <td className="py-3 px-4">{item.specifications || ''}</td>
                                <td className="py-3 px-4">{item.frequency || ''}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData.trimmingObservations?.[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation1', e.target.value, true)}
                                        placeholder="Enter observation 1"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData.trimmingObservations?.[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation2', e.target.value, true)}
                                        placeholder="Enter observation 2"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Visual Inspection Table */}
            <h4 className="text-lg font-semibold mt-6 mb-4 text-gray-700">Visual Inspection</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white rounded-lg border-collapse table-auto">
                    <thead>
                        <tr className="bg-[#424752] text-white text-center">
                            <th className="py-3 px-4 min-w-[150px] rounded-tl-lg">Product</th>
                            <th className="py-3 px-4 min-w-[200px]">Specifications</th>
                            <th className="py-3 px-4 min-w-[150px]">Frequency</th>
                            <th className="py-3 px-4 min-w-[200px]">Observation 1</th>
                            <th className="py-3 px-4 min-w-[200px] rounded-tr-lg">Observation 2</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visualInspectionData.map((item, index) => (
                            <tr
                                key={index}
                                className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100`}
                            >
                                <td className="py-3 px-4">{item.product || ''}</td>
                                <td className="py-3 px-4">{item.specifications || ''}</td>
                                <td className="py-3 px-4">{item.frequency || ''}</td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData.visualObservations?.[index]?.observation1 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation1', e.target.value, false)}
                                        placeholder="Enter observation 1"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                                <td className="py-3 px-4">
                                    <input
                                        type="text"
                                        value={formData.visualObservations?.[index]?.observation2 || ''}
                                        onChange={(e) => handleObservationChange(index, 'observation2', e.target.value, false)}
                                        placeholder="Enter observation 2"
                                        className="w-full border p-2 rounded-lg focus:outline-none"
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Non Conformance Input */}
            <div className="mt-6">
                <h4 className="text-lg font-semibold mb-2 text-gray-700">Non Conformance</h4>
                <textarea
                    value={formData.nonConformance || ''}
                    onChange={handleNonConformanceChange}
                    placeholder="Enter details about non conformance..."
                    className="w-full border p-4 rounded-lg focus:outline-none"
                    rows={4}
                />
            </div>

            {/* Submit Observations */}
            <button
                onClick={submitObservations}
                className="mt-4 px-6 py-2 bg-[#424752] text-white rounded-lg hover:bg-gray-500"
            >
                Submit
            </button>
        </div>
    );
};

export default Trimming;